import {
  PaymentOptionContentType,
  PaymentOptionsContent,
} from 'lib/types/payment';

type LocalPaymentOption = {
  content: PaymentOptionsContent;
  summary: string;
};

export type DivisionPaymentOptionConfig = {
  notice: {
    content: string;
    popup: string;
  };
  options: Partial<Record<PaymentOptionContentType, LocalPaymentOption>>;
};

const createGwatGetawaysPaymentOptionConfig = (
  formattedUnitAmount: string
): DivisionPaymentOptionConfig => ({
  notice: {
    content: 'Small groups. Maximum 14 travellers.',
    popup:
      'Our standard Getaways are capped at 14 travellers, excluding your guide. Creator-hosted Getaways have custom group sizes, which may be larger or smaller. Check the trip page for details.',
  },
  options: {
    Promotional: {
      content: {
        title: 'Tentative Deposit',
        type: 'Promotional',
        entries: [
          {
            content:
              "We'll contact you to answer your questions &amp; help plan your Getaway.",
          },
          {
            content: `Your ${formattedUnitAmount} goes towards your Booking Deposit.`,
          },
          { content: 'No further automatic card charges at this stage.' },
          {
            content:
              'Complete your Booking Deposit when you’re ready to secure your spot.',
          },
        ],
      },
      summary: 'This registers your interest. It doesn’t reserve a spot.',
    },
    Standard: {
      content: {
        title: 'Booking Deposit',
        type: 'Standard',
        entries: [
          { content: 'Secure your spot on this departure.' },
          {
            content:
              'Change your date or destination up to 61 days before departure.',
            popup: {
              content:
                'You can change your date or destination for free with your Trip Coordinator up to 61 days before departure. (TCs apply)',
            },
          },
          {
            content: 'Remaining trip balance charged 90 days before departure.',
          },
          {
            content: 'Protected by our Lifetime Deposit Guarantee.',
            popup: {
              content:
                'Swap your trip, gift it or save it for later with our Lifetime Deposit Guarantee. (TCs apply)',
            },
          },
        ],
      },
      summary: 'This confirms your spot on this trip.',
    },
  },
});

export const gwatGetawaysPaymentOptionConfig =
  createGwatGetawaysPaymentOptionConfig('$1');

export const getDivisionPaymentOptionConfig = (
  tenant: string,
  division: string | undefined,
  formattedUnitAmount = '$1'
): DivisionPaymentOptionConfig | null => {
  if (tenant !== 'gwat' || division?.trim().toLowerCase() !== 'getaways') {
    return null;
  }

  return formattedUnitAmount === '$1'
    ? gwatGetawaysPaymentOptionConfig
    : createGwatGetawaysPaymentOptionConfig(formattedUnitAmount);
};

// Configuration for custom payment option content based on trip codes
export const customPaymentOptionConfig: Record<
  string,
  Partial<
    Record<
      'Promotional' | 'Standard' | 'VIP' | 'LITE-VIP',
      PaymentOptionsContent
    >
  >
> = {
  'WH-AA': {
    VIP: {
      title: 'VIP Booking',
      type: 'VIP',
      entries: [
        {
          content: 'Trip start date guaranteed.',
        },
        {
          content: 'Instant access to Local Jobs.',
        },
        {
          content: 'Meet others now in Social.',
        },
        {
          content: 'Date, length & destination still changeable. ',
        },
        {
          content: '100% refundable if you don’t qualify today.',
        },
      ],
    },
  },
  // Add more trip codes and their custom content here
  // Example for future use:
  // 'WH-UK': {
  //   Standard: {
  //     title: 'Standard Booking',
  //     type: 'Standard',
  //     entries: [
  //       { content: 'Custom content for WH-UK Standard...' },
  //     ],
  //   },
  //   VIP: {
  //     title: 'VIP Booking',
  //     type: 'VIP',
  //     entries: [
  //       { content: 'Custom content for WH-UK VIP...' },
  //     ],
  //   },
  // },
};

/**
 * Get custom payment option content for a specific trip code and type
 * @param tripCode - The trip code (e.g., 'WH-AA')
 * @param type - The payment option type ('Promotional', 'Standard', 'VIP', 'LITE-VIP')
 * @returns Custom content if configured, otherwise null
 */
export const getCustomPaymentOptionContent = (
  tripCode: string | undefined,
  type: 'Promotional' | 'Standard' | 'VIP' | 'LITE-VIP'
): PaymentOptionsContent | null => {
  if (!tripCode || !customPaymentOptionConfig[tripCode]) {
    return null;
  }

  return customPaymentOptionConfig[tripCode][type] || null;
};
